// Pure edit-layer for local panel board layout editing.
// No React imports, no API calls, no side effects.
// buildEditableLayout wraps buildPanelLayout; all other functions operate on EditablePanelLayout.

import type { BoardLayout, Circuit, RCDGroup } from '@/types/api';
import { buildPanelLayout } from './layoutModel';
import type {
  DevicePlacement,
  EditablePanelLayout,
  PanelDevice,
  PanelLayout,
  PanelRow,
  PanelSlot,
} from './types';

function posKey(row: number, col: number): string {
  return `${row}:${col}`;
}

function isC13Circuit(circuit: Circuit | undefined): boolean {
  if (!circuit) return false;
  return [circuit.rebt_code, circuit.circuit_id, circuit.label]
    .some((value) => /\bC13\b/i.test(value ?? ''));
}

function groupHasC13(group: RCDGroup | undefined, circuitsById: ReadonlyMap<string, Circuit>): boolean {
  if (!group) return false;
  return group.circuit_ids.some((circuitId) => isC13Circuit(circuitsById.get(circuitId)));
}

export const MAX_CIRCUITS_PER_RCD_GROUP = 5;

export type CircuitGroupMoveBlockReason =
  | 'c13_dedicated'
  | 'target_group_full'
  | 'invalid';

export type RcdGroupDeleteBlockReason =
  | 'c13_dedicated'
  | 'invalid';

// Single-line spacing, in modules. Mirrors rendering/layout_engine.py _SINGLE_LINE_POLICY:
// 18mm gap == 1 module (visual_module_width_mm = 18). Devices within a group are flush.
const GAP_AFTER_INCOMING = 1;  // after_incoming_gap_mm / incoming_to_first_linea_gap_mm
const GAP_BETWEEN_GROUPS = 1;  // between_linea_gap_mm / inter_linea_gap_mm

// Ordered device sequence mirroring domain/board_layout.py build order:
// incoming breaker first, then each RCD group (RCD, then its MCBs in circuit order).
// The backend packs these densely; here we recover the grouping so the editor can
// re-lay them with the single-line gaps the user assembles from.
function orderDevicesByGroup(
  boardLayout: BoardLayout,
  devices: ReadonlyMap<string, PanelDevice>,
): { incomingId: string | null; groups: string[][] } {
  const incoming = [...devices.values()].find(d => d.deviceType === 'incoming_breaker') ?? null;
  const incomingId = incoming?.deviceId ?? null;

  const circuitById = new Map(boardLayout.circuits.map(c => [c.circuit_id, c]));
  const sortedGroups = [...boardLayout.groups].sort((a, b) =>
    a.group_id < b.group_id ? -1 : a.group_id > b.group_id ? 1 : 0,
  );

  const placed = new Set<string>();
  if (incomingId) placed.add(incomingId);

  const groups: string[][] = [];
  for (const g of sortedGroups) {
    const seq: string[] = [];
    if (g.protection_device_id && devices.has(g.protection_device_id) && !placed.has(g.protection_device_id)) {
      seq.push(g.protection_device_id);
      placed.add(g.protection_device_id);
    }
    for (const cid of g.circuit_ids) {
      const dId = circuitById.get(cid)?.protection_device_id;
      if (dId && devices.has(dId) && !placed.has(dId)) {
        seq.push(dId);
        placed.add(dId);
      }
    }
    if (seq.length) groups.push(seq);
  }

  // Any device not referenced by a group (data gap) is appended flush so it stays visible.
  const leftovers = [...devices.keys()].filter(id => !placed.has(id));
  if (leftovers.length) groups.push(leftovers);

  return { incomingId, groups };
}

export interface BuildEditableOptions {
  // When true, omit the single-line preview gaps (after the incoming breaker and
  // between groups) so devices sit flush. The backend inserts these gaps for the
  // preview render; counting them as real modules over-sizes the enclosure auto-fit.
  // The editor's "remove empty modules" toggle builds with compact: true so sizing
  // reflects the real module count.
  compact?: boolean;
}

// Build mutable edit state from backend data.
// Devices and diagnostics come from buildPanelLayout (which applies effective module
// widths, e.g. the 3-module IGA). Placements are then re-laid as a single-line row:
// IGA + gap + group + gap + group … with trailing reserve, instead of the dense
// backend packing. Gaps are plain empty slots the user can drop devices into, unless
// options.compact drops them so the row is flush.
//
// gridModulesPerRow overrides the backend modules_per_row so the editor can match a
// chosen enclosure's rail width (e.g. 12). Reflow wraps onto the next rail as needed.
export function buildEditableLayout(
  boardLayout: BoardLayout,
  gridModulesPerRow?: number,
  options?: BuildEditableOptions,
): EditablePanelLayout {
  const panelLayout = buildPanelLayout(boardLayout);
  const modulesPerRow = gridModulesPerRow && gridModulesPerRow > 0 ? gridModulesPerRow : panelLayout.modulesPerRow;
  const gapAfterIncoming = options?.compact ? 0 : GAP_AFTER_INCOMING;
  const gapBetweenGroups = options?.compact ? 0 : GAP_BETWEEN_GROUPS;

  // Collect devices (with effective widths) and the backend reserve count.
  const devices = new Map<string, PanelDevice>();
  let reserveCount = 0;
  for (const row of panelLayout.rows) {
    for (const slot of row.slots) {
      if (slot.kind === 'device_anchor' && slot.device) devices.set(slot.device.deviceId, slot.device);
      if (slot.kind === 'reserve') reserveCount++;
    }
  }

  // ── Re-lay devices left-to-right with single-line gaps ──
  const { incomingId, groups } = orderDevicesByGroup(boardLayout, devices);
  const placements = new Map<string, DevicePlacement>();

  let pos = 0;
  const widthOf = (id: string) => Math.max(1, devices.get(id)?.modulesWidth ?? 1);
  // Keep a device from straddling a row boundary by padding to the next row start.
  const avoidStraddle = (w: number) => {
    const col = pos % modulesPerRow;
    if (w <= modulesPerRow && col + w > modulesPerRow) pos += modulesPerRow - col;
  };
  const place = (id: string) => {
    const w = widthOf(id);
    avoidStraddle(w);
    placements.set(id, { row: Math.floor(pos / modulesPerRow), col: pos % modulesPerRow });
    pos += w;
  };

  if (incomingId) {
    place(incomingId);
    if (groups.length) pos += gapAfterIncoming;
  }
  groups.forEach((seq, groupIdx) => {
    if (groupIdx > 0) pos += gapBetweenGroups;
    for (const id of seq) place(id);
  });

  // ── Trailing reserve slots (1-wide each), preserving the backend reserve count ──
  const reservePositions = new Set<string>();
  for (let i = 0; i < reserveCount; i++) {
    reservePositions.add(posKey(Math.floor(pos / modulesPerRow), pos % modulesPerRow));
    pos += 1;
  }

  return {
    layoutId: panelLayout.layoutId,
    modulesPerRow,
    totalRows: Math.max(1, Math.ceil(pos / modulesPerRow)),
    devices,
    placements,
    reservePositions,
    // Editable clone of the backend circuits; local line edits mutate this copy only.
    circuits: boardLayout.circuits.map((c) => ({ ...c })),
    groups: boardLayout.groups.map((g) => ({ ...g, circuit_ids: [...g.circuit_ids], warnings: [...g.warnings] })),
    warnings: panelLayout.warnings,
  };
}

export function reflowEditableLayout(
  editable: EditablePanelLayout,
  gridModulesPerRow: number,
  options?: BuildEditableOptions,
): EditablePanelLayout {
  const modulesPerRow = gridModulesPerRow > 0 ? gridModulesPerRow : editable.modulesPerRow;
  const gapBetweenGroups = options?.compact ? 0 : GAP_BETWEEN_GROUPS;
  const placements = new Map<string, DevicePlacement>();

  let pos = 0;
  const widthOf = (id: string) => Math.max(1, editable.devices.get(id)?.modulesWidth ?? 1);
  const avoidStraddle = (w: number) => {
    const col = pos % modulesPerRow;
    if (w <= modulesPerRow && col + w > modulesPerRow) pos += modulesPerRow - col;
  };
  const place = (id: string) => {
    if (!editable.devices.has(id)) return;
    const w = widthOf(id);
    avoidStraddle(w);
    placements.set(id, { row: Math.floor(pos / modulesPerRow), col: pos % modulesPerRow });
    pos += w;
  };

  const incomingId = [...editable.devices.values()].find((d) => d.deviceType === 'incoming_breaker')?.deviceId ?? null;
  if (incomingId) place(incomingId);

  editable.groups.forEach((group, groupIdx) => {
    if (incomingId || groupIdx > 0) pos += groupIdx === 0 && incomingId ? (options?.compact ? 0 : GAP_AFTER_INCOMING) : gapBetweenGroups;
    place(group.protection_device_id);
    for (const circuitId of group.circuit_ids) {
      const circuit = editable.circuits.find((c) => c.circuit_id === circuitId);
      if (circuit) place(circuit.protection_device_id);
    }
  });

  const reservePositions = new Set<string>();
  for (let i = 0; i < editable.reservePositions.size; i++) {
    reservePositions.add(posKey(Math.floor(pos / modulesPerRow), pos % modulesPerRow));
    pos += 1;
  }

  return {
    ...editable,
    modulesPerRow,
    totalRows: Math.max(1, Math.ceil(pos / modulesPerRow)),
    placements,
    reservePositions,
  };
}

// Returns the deviceId occupying (row, col), or null if the slot is free.
export function getDeviceAt(
  editable: EditablePanelLayout,
  row: number,
  col: number,
): string | null {
  for (const [deviceId, placement] of editable.placements) {
    const device = editable.devices.get(deviceId);
    if (!device) continue;
    if (
      placement.row === row &&
      col >= placement.col &&
      col < placement.col + device.modulesWidth
    ) {
      return deviceId;
    }
  }
  return null;
}

// True iff a `width`-module device anchored at (row, col) stays inside one row and
// only overlaps slots that are free or occupied by one of `ignore`.
function spanFits(
  editable: EditablePanelLayout,
  row: number,
  col: number,
  width: number,
  ignore: ReadonlySet<string>,
): boolean {
  if (row < 0 || row >= editable.totalRows) return false;
  if (col < 0 || col + width > editable.modulesPerRow) return false;
  for (let c = col; c < col + width; c++) {
    const occupant = getDeviceAt(editable, row, c);
    if (occupant !== null && !ignore.has(occupant)) return false;
  }
  return true;
}

// Returns true iff placing deviceId anchored at (targetRow, targetCol) is valid:
// - within grid bounds, no cross-row span, no overlap with a different device
export function canPlaceDevice(
  editable: EditablePanelLayout,
  deviceId: string,
  targetRow: number,
  targetCol: number,
): boolean {
  const device = editable.devices.get(deviceId);
  if (!device) return false;
  return spanFits(editable, targetRow, targetCol, device.modulesWidth, new Set([deviceId]));
}

// Returns true iff two devices can exchange anchor positions: each must fit in the
// other's footprint without colliding with a third device. Same-width devices always
// can; different widths only when the wider one's span at the other's anchor is clear.
// This lets the user rearrange a full panel where no free slot exists.
export function canSwapDevices(
  editable: EditablePanelLayout,
  aId: string,
  bId: string,
): boolean {
  if (aId === bId) return false;
  const a = editable.devices.get(aId);
  const b = editable.devices.get(bId);
  if (!a || !b) return false;
  const pa = editable.placements.get(aId);
  const pb = editable.placements.get(bId);
  if (!pa || !pb) return false;

  const ignore = new Set([aId, bId]);
  return (
    spanFits(editable, pb.row, pb.col, a.modulesWidth, ignore) &&
    spanFits(editable, pa.row, pa.col, b.modulesWidth, ignore)
  );
}

// Pure: sets a device's ampere rating, else same reference on a no-op/invalid value.
// modulesWidth (driven by pole_count) is unchanged. For MCBs the rendered SVG symbol
// follows ratingA through resolvePanelSymbol(symbol_mcb, ratingA); ratings without an
// asset fall back to the labeled rectangle.
export function setDeviceRating(
  editable: EditablePanelLayout,
  deviceId: string,
  ratingA: number,
): EditablePanelLayout {
  if (!Number.isFinite(ratingA) || ratingA <= 0) return editable;
  const device = editable.devices.get(deviceId);
  if (!device || device.ratingA === ratingA) return editable;

  const newDevices = new Map(editable.devices);
  newDevices.set(deviceId, { ...device, ratingA });
  return { ...editable, devices: newDevices };
}

// Pure: swaps the two devices' anchor positions on a valid swap, else same reference.
export function swapDevices(
  editable: EditablePanelLayout,
  aId: string,
  bId: string,
): EditablePanelLayout {
  if (!canSwapDevices(editable, aId, bId)) return editable;

  const pa = editable.placements.get(aId)!;
  const pb = editable.placements.get(bId)!;
  const newPlacements = new Map(editable.placements);
  newPlacements.set(aId, { row: pb.row, col: pb.col });
  newPlacements.set(bId, { row: pa.row, col: pa.col });
  return { ...editable, placements: newPlacements };
}

// Pure: returns a new EditablePanelLayout on valid move, the same reference on invalid.
export function moveDevice(
  editable: EditablePanelLayout,
  deviceId: string,
  targetRow: number,
  targetCol: number,
): EditablePanelLayout {
  if (!canPlaceDevice(editable, deviceId, targetRow, targetCol)) return editable;

  const newPlacements = new Map(editable.placements);
  newPlacements.set(deviceId, { row: targetRow, col: targetCol });
  return { ...editable, placements: newPlacements };
}

const EMPTY_IGNORE: ReadonlySet<string> = new Set();

// True iff a new `width`-module breaker can be inserted anchored at (row, col):
// a free run of `width` slots inside one row.
export function canInsertBreakerAt(
  editable: EditablePanelLayout,
  row: number,
  col: number,
  width: number,
): boolean {
  return spanFits(editable, row, col, Math.max(1, width), EMPTY_IGNORE);
}

// Smallest `${prefix}${n}` (n ≥ 1) not already present in `existing`.
function nextLocalId(prefix: string, existing: ReadonlySet<string>): string {
  let n = 1;
  while (existing.has(`${prefix}${n}`)) n++;
  return `${prefix}${n}`;
}

// Circuit (line) whose breaker is deviceId, or null.
export function getCircuitForDevice(editable: EditablePanelLayout, deviceId: string): Circuit | null {
  return editable.circuits.find((c) => c.protection_device_id === deviceId) ?? null;
}

// Pure: renames a circuit (line), else same reference on no-op/unknown id.
export function setCircuitLabel(
  editable: EditablePanelLayout,
  circuitId: string,
  label: string,
): EditablePanelLayout {
  const circuit = editable.circuits.find((c) => c.circuit_id === circuitId);
  if (!circuit || circuit.label === label) return editable;
  const circuits = editable.circuits.map((c) =>
    c.circuit_id === circuitId ? { ...c, label } : c,
  );
  return { ...editable, circuits };
}

export function getGroupForCircuit(editable: EditablePanelLayout, circuitId: string): RCDGroup | null {
  return editable.groups.find((g) => g.circuit_ids.includes(circuitId)) ?? null;
}

// C13 EV charging must remain a dedicated group by rule:
// - C13 itself cannot move to another group.
// - Other circuits cannot move into a group that contains C13.
export function getCircuitGroupMoveBlockReason(
  editable: EditablePanelLayout,
  circuitId: string,
  targetGroupId: string,
): CircuitGroupMoveBlockReason | null {
  const circuit = editable.circuits.find((c) => c.circuit_id === circuitId);
  if (!circuit) return 'invalid';
  if (isC13Circuit(circuit)) return 'c13_dedicated';

  const currentGroup = getGroupForCircuit(editable, circuitId);
  if (currentGroup?.group_id === targetGroupId) return null;

  const circuitsById = new Map(editable.circuits.map((c) => [c.circuit_id, c]));
  const targetGroup = editable.groups.find((g) => g.group_id === targetGroupId);
  if (!targetGroup) return 'invalid';
  if (groupHasC13(targetGroup, circuitsById)) return 'c13_dedicated';
  if (targetGroup.circuit_ids.length >= MAX_CIRCUITS_PER_RCD_GROUP) return 'target_group_full';

  return null;
}

export function canMoveCircuitToGroup(
  editable: EditablePanelLayout,
  circuitId: string,
  targetGroupId: string,
): boolean {
  const currentGroup = getGroupForCircuit(editable, circuitId);
  return currentGroup?.group_id !== targetGroupId &&
    getCircuitGroupMoveBlockReason(editable, circuitId, targetGroupId) === null;
}

export function moveCircuitToGroup(
  editable: EditablePanelLayout,
  circuitId: string,
  targetGroupId: string,
): EditablePanelLayout {
  if (!canMoveCircuitToGroup(editable, circuitId, targetGroupId)) return editable;

  const groups = editable.groups.map((group) => {
    const withoutCircuit = group.circuit_ids.filter((id) => id !== circuitId);
    if (group.group_id !== targetGroupId) return { ...group, circuit_ids: withoutCircuit };
    return { ...group, circuit_ids: [...withoutCircuit, circuitId] };
  });
  return { ...editable, groups };
}

function firstAvailablePlacement(editable: EditablePanelLayout, width: number): DevicePlacement {
  for (let row = 0; row < editable.totalRows; row++) {
    for (let col = 0; col <= editable.modulesPerRow - width; col++) {
      if (spanFits(editable, row, col, width, EMPTY_IGNORE)) return { row, col };
    }
  }
  return { row: editable.totalRows, col: 0 };
}

export function createRcdGroupForCircuit(
  editable: EditablePanelLayout,
  circuitId: string,
): EditablePanelLayout {
  const circuit = editable.circuits.find((c) => c.circuit_id === circuitId);
  if (!circuit || isC13Circuit(circuit)) return editable;

  const deviceId = nextLocalId('local_rcd_', new Set(editable.devices.keys()));
  const groupId = nextLocalId('local_group_', new Set(editable.groups.map((g) => g.group_id)));
  const placement = firstAvailablePlacement(editable, 2);

  const devices = new Map(editable.devices);
  devices.set(deviceId, {
    deviceId,
    moduleId: deviceId,
    deviceType: 'rcd',
    ratingA: 40,
    poleCount: 2,
    modulesWidth: 2,
    symbolId: 'symbol_rcd',
    rcdSensitivityMa: 30,
  });

  const placements = new Map(editable.placements);
  placements.set(deviceId, placement);

  const groups = editable.groups
    .map((group) => ({ ...group, circuit_ids: group.circuit_ids.filter((id) => id !== circuitId) }))
    .concat({
      group_id: groupId,
      label: `ID Extra ${editable.groups.length + 1}`,
      circuit_ids: [circuitId],
      protection_device_id: deviceId,
      warnings: [],
    });

  return {
    ...editable,
    totalRows: Math.max(editable.totalRows, placement.row + 1),
    devices,
    placements,
    groups,
  };
}

export function getRcdGroupDeleteBlockReason(
  editable: EditablePanelLayout,
  groupId: string,
): RcdGroupDeleteBlockReason | null {
  const circuitsById = new Map(editable.circuits.map((c) => [c.circuit_id, c]));
  const group = editable.groups.find((g) => g.group_id === groupId);
  if (!group) return 'invalid';
  if (groupHasC13(group, circuitsById)) return 'c13_dedicated';
  if (group.circuit_ids.length > 0) return 'invalid';
  return null;
}

export function canDeleteRcdGroup(
  editable: EditablePanelLayout,
  groupId: string,
): boolean {
  return getRcdGroupDeleteBlockReason(editable, groupId) === null;
}

export function deleteRcdGroup(
  editable: EditablePanelLayout,
  groupId: string,
): EditablePanelLayout {
  if (!canDeleteRcdGroup(editable, groupId)) return editable;
  const group = editable.groups.find((g) => g.group_id === groupId);
  if (!group) return editable;

  const devices = new Map(editable.devices);
  devices.delete(group.protection_device_id);

  const placements = new Map(editable.placements);
  placements.delete(group.protection_device_id);

  const groups = editable.groups.filter((item) => item.group_id !== groupId);

  return { ...editable, devices, placements, groups };
}

// Inserts a new MCB (poleCount → modulesWidth) and its line at (row, col) when it fits,
// returning the new device id so the caller can select it. Returns null if the slot
// can't hold the breaker. lineLabel is the new line's name (e.g. "C5a").
export function addBreaker(
  editable: EditablePanelLayout,
  options: { ratingA: number; poleCount: number; lineLabel: string; row: number; col: number },
): { editable: EditablePanelLayout; deviceId: string } | null {
  const width = Math.max(1, options.poleCount);
  if (!canInsertBreakerAt(editable, options.row, options.col, width)) return null;

  const deviceId = nextLocalId('local_mcb_', new Set(editable.devices.keys()));
  const circuitId = nextLocalId('local_circuit_', new Set(editable.circuits.map((c) => c.circuit_id)));

  const device: PanelDevice = {
    deviceId,
    moduleId: deviceId,
    deviceType: 'mcb',
    ratingA: options.ratingA,
    poleCount: width,
    modulesWidth: width,
    symbolId: 'symbol_mcb',
    rcdSensitivityMa: null,
  };
  const circuit: Circuit = {
    circuit_id: circuitId,
    rebt_code: null,
    label: options.lineLabel,
    category: 'sockets',
    load_ids: [],
    protection_device_id: deviceId,
    is_dedicated: false,
    is_wet_zone: false,
  };

  const devices = new Map(editable.devices);
  devices.set(deviceId, device);
  const placements = new Map(editable.placements);
  placements.set(deviceId, { row: options.row, col: options.col });

  return {
    editable: { ...editable, devices, placements, circuits: [...editable.circuits, circuit] },
    deviceId,
  };
}

// Derives a PanelLayout display grid from current editable state for use by PanelBoardCanvas.
// Reserve slots that receive a device are shown as device slots; freed device slots show as empty.
export function editableToDisplayLayout(editable: EditablePanelLayout): PanelLayout {
  // Build position → deviceId lookup from current placements.
  const deviceAtPos = new Map<string, string>();
  for (const [deviceId, placement] of editable.placements) {
    const device = editable.devices.get(deviceId);
    if (!device) continue;
    for (let c = placement.col; c < placement.col + device.modulesWidth; c++) {
      deviceAtPos.set(posKey(placement.row, c), deviceId);
    }
  }

  const rows: PanelRow[] = [];
  for (let rowIdx = 0; rowIdx < editable.totalRows; rowIdx++) {
    const slots: PanelSlot[] = [];
    for (let colIdx = 0; colIdx < editable.modulesPerRow; colIdx++) {
      const key = posKey(rowIdx, colIdx);
      const deviceId = deviceAtPos.get(key);

      if (deviceId) {
        const device = editable.devices.get(deviceId)!;
        const placement = editable.placements.get(deviceId)!;
        const isAnchor = placement.row === rowIdx && placement.col === colIdx;
        slots.push(
          isAnchor
            ? { columnIndex: colIdx, kind: 'device_anchor', device }
            : { columnIndex: colIdx, kind: 'device_continuation' },
        );
      } else if (editable.reservePositions.has(key)) {
        slots.push({ columnIndex: colIdx, kind: 'reserve' });
      } else {
        slots.push({ columnIndex: colIdx, kind: 'empty' });
      }
    }
    rows.push({ rowIndex: rowIdx, slots });
  }

  return {
    layoutId: editable.layoutId,
    modulesPerRow: editable.modulesPerRow,
    rows,
    warnings: [...editable.warnings],
  };
}
